import { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { UNSAFE_NavigationContext, useLocation } from 'react-router-dom';
import useBeforeUnloadPrompt from './useBeforeUnloadPrompt';

const DEFAULT_MESSAGE = 'You have unsaved changes. Leave this page without saving them?';

/*
 * One popstate listener for every guard, registered when this module loads. The Sidebar
 * imports this module eagerly, so the listener is in place before <BrowserRouter> attaches
 * its own, and listeners on one target run in registration order: it sees Back and Forward
 * before the router in every engine. Capture-phase ordering on window differs between
 * engines (some run capture listeners in registration order), so it is not relied on.
 */
const popHandlers = []; // the active guards' handlers, newest last
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (e) => {
    const handle = popHandlers[popHandlers.length - 1];
    if (handle) handle(e);
  });
}

// Message refs of the mounted guards whose `when` is true, newest last.
const activeGuards = [];

// Set by confirmLeave(): the next guarded push/replace that leaves the page goes through
// without asking. Cleared when taken, or at the end of the current task if none follows.
let leavePass = null;
// True while a guarded navigation that was allowed runs, so guards wrapped inside it pass.
let passing = false;

const takeLeavePass = () => {
  if (!leavePass) return false;
  leavePass = null;
  return true;
};

/**
 * Asks before a control changes app state and then navigates away, such as logout. Call
 * it before the state change: once the change renders, the page can unmount before the
 * navigation is reached, and a prompt at that point can no longer keep the user on it.
 *
 * Returns true when no guard is active or the user confirms. On a confirm, the next router
 * push or replace that leaves the page, in the same task, is not asked again.
 */
export function confirmLeave() {
  if (!activeGuards.length) return true;
  // eslint-disable-next-line no-alert
  if (!window.confirm(activeGuards[activeGuards.length - 1].current)) return false;
  const pass = {};
  leavePass = pass;
  setTimeout(() => { if (leavePass === pass) leavePass = null; }, 0);
  return true;
}

// The router's index of a history entry, which it writes into history.state; null when absent.
const entryIndex = (state) => (state && typeof state.idx === 'number' ? state.idx : null);

// A pathname percent-encoded the way window.location reports it, or null.
const encodedPathname = (path) => {
  try {
    return new URL(path, 'http://localhost').pathname;
  } catch {
    return null;
  }
};

// Whether a navigation to `to` changes the pathname of the navigator's current location.
// A `to` without a pathname stays on it.
const leavesPathname = (navigator, to) => {
  const next = typeof to === 'string' ? to.split(/[?#]/)[0] : to && to.pathname;
  if (!next) return false;
  const current = (navigator.location && navigator.location.pathname) || window.location.pathname;
  return encodedPathname(next) !== encodedPathname(current);
};

/**
 * Asks before the user leaves the page while `when` is true.
 *
 * - Tab close, reload, a typed URL, and in the desktop shell quit and Restart now:
 *   `beforeunload`, through useBeforeUnloadPrompt: the browser's own prompt, or the shell's
 *   Leave / Stay dialog. A desktop shell without that dialog would cancel the close without
 *   asking, so there nothing is registered and the app closes without asking.
 * - In-app navigation (links, the sidebar, navigate()): the router's navigator push and
 *   replace are wrapped. The app uses <BrowserRouter>, which has no useBlocker. A
 *   navigation that keeps the pathname passes: it comes from the page itself (a query
 *   change), and the page handles what that does to its state.
 * - Back and Forward: the module's popstate listener runs before the router's and stops
 *   it. On Cancel the history moves back to this entry; the popstate from that move is
 *   stopped as well, so the router never sees either. On Leave the event is dispatched
 *   again for the router. A move to the same path and query (a hash) passes. Skipped when
 *   either entry carries no router index (no way to undo the move).
 * - A control that changes app state and then navigates (logout) calls confirmLeave()
 *   before the state change; see there.
 *
 * The prompt is window.confirm: the navigator wrap and the popstate handler decide
 * synchronously.
 */
export default function useUnsavedChangesGuard(when, message = DEFAULT_MESSAGE) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const location = useLocation();
  const messageRef = useRef(message);
  messageRef.current = message;

  // The history entry this page shows: its router index and its path + query.
  const entryRef = useRef({ idx: null, href: '' });
  useLayoutEffect(() => {
    entryRef.current = {
      idx: entryIndex(window.history.state),
      href: window.location.pathname + window.location.search,
    };
  }, [location]);

  useBeforeUnloadPrompt(when);

  useEffect(() => {
    if (!when) return undefined;
    const entry = messageRef;
    activeGuards.push(entry);
    return () => {
      const i = activeGuards.lastIndexOf(entry);
      if (i !== -1) activeGuards.splice(i, 1);
    };
  }, [when]);

  useEffect(() => {
    if (!when || !navigator) return undefined;
    const { push, replace } = navigator;
    let active = true;
    const guard = (original) => (to, ...rest) => {
      if (!active || passing || !leavesPathname(navigator, to)) {
        original.call(navigator, to, ...rest);
        return;
      }
      // eslint-disable-next-line no-alert
      if (!takeLeavePass() && !window.confirm(messageRef.current)) return;
      passing = true;
      try {
        original.call(navigator, to, ...rest);
      } finally {
        passing = false;
      }
    };
    const guardedPush = guard(push);
    const guardedReplace = guard(replace);
    navigator.push = guardedPush;
    navigator.replace = guardedReplace;
    return () => {
      active = false;
      // Restored only when still ours; a wrapper added on top keeps working, and ours inside
      // it passes everything through once inactive.
      if (navigator.push === guardedPush) navigator.push = push;
      if (navigator.replace === guardedReplace) navigator.replace = replace;
    };
  }, [when, navigator]);

  useEffect(() => {
    if (!when) return undefined;
    let revertingTo = null; // index of the entry a Cancel is moving back to
    let replaying = false;
    const onPopState = (e) => {
      if (replaying) return;
      const nextIdx = entryIndex(e.state);
      if (revertingTo !== null) {
        const expected = revertingTo;
        revertingTo = null;
        if (nextIdx === expected) {
          e.stopImmediatePropagation();
          return;
        }
      }
      const { idx: currentIdx, href } = entryRef.current;
      if (nextIdx === null || currentIdx === null || nextIdx === currentIdx) return;
      if (window.location.pathname + window.location.search === href) return;
      e.stopImmediatePropagation();
      // eslint-disable-next-line no-alert
      if (window.confirm(messageRef.current)) {
        replaying = true;
        try {
          window.dispatchEvent(new PopStateEvent('popstate', { state: e.state }));
        } finally {
          replaying = false;
        }
        return;
      }
      revertingTo = currentIdx;
      window.history.go(currentIdx - nextIdx);
    };
    popHandlers.push(onPopState);
    return () => {
      const i = popHandlers.lastIndexOf(onPopState);
      if (i !== -1) popHandlers.splice(i, 1);
    };
  }, [when]);
}
